/**
 * Nettoyage du HTML écrit par un membre, juste avant son affichage.
 *
 * POURQUOI ICI ET PAS À LA SAISIE : l'éditeur filtre dans le navigateur, mais
 * l'API enregistre ce qu'on lui envoie. Tout ce qui est déjà en base, et tout
 * ce qu'un appel direct y déposera, passe donc par l'affichage. C'est le seul
 * endroit par lequel chaque message passe forcément.
 *
 * POURQUOI SUR L'ARBRE ET PAS SUR LA CHAÎNE : helpers/transformHtml.js
 * réécrit le HTML avant de l'analyser (blocs de code, iframes tapées en
 * texte). Nettoyer la chaîne d'abord laisserait ces réécritures fabriquer une
 * balise après coup. On nettoie donc l'arbre déjà analysé, celui que React va
 * rendre tel quel : rien ne peut s'y ajouter ensuite.
 *
 * La liste des balises et des attributs gardés vient d'un relevé des
 * 3 327 messages, réponses et corrections en base le 05/10/2026 : tout ce
 * que l'éditeur produit y figure (texte, listes, liens, images, tableaux,
 * couleurs, blocs de code), et rien de ce qui est retiré n'y était utilisé,
 * hors restes de copier-coller sans effet à l'écran.
 *
 * Ce fichier ne dépend de rien, pour être relu et éprouvé seul.
 */

// Balises gardées. Leurs attributs sont filtrés plus bas.
const ALLOWED_TAGS = new Set([
    "p", "br", "hr", "span", "div",
    "strong", "b", "em", "i", "u", "s", "strike", "del", "ins",
    "sub", "sup", "mark", "small", "abbr", "cite", "q",
    "code", "pre", "kbd", "samp", "var",
    "blockquote",
    "ul", "ol", "li", "dl", "dt", "dd",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "a", "img", "figure", "figcaption",
    "table", "thead", "tbody", "tfoot", "tr", "td", "th",
    "caption", "colgroup", "col",
    "details", "summary",
    "article", "section", "aside", "header", "footer", "main", "nav",
    "address",
]);

// Balises retirées avec tout ce qu'elles contiennent : elles exécutent du
// code, chargent une ressource, posent un formulaire ou recouvrent la page.
const DROPPED_TAGS = new Set([
    "script", "style", "iframe", "frame", "frameset", "object", "embed",
    "applet", "link", "meta", "base", "head", "title", "template",
    "noscript", "noembed", "noframes", "svg", "math",
    "input", "button", "select", "option", "optgroup", "textarea",
    "datalist", "audio", "video", "source", "track", "canvas", "dialog",
    "map", "area", "portal", "slot",
]);

// Toute autre balise (inconnue, <font>, <form>, élément personnalisé…) est
// retirée, mais son contenu reste : le texte d'un collage n'est pas perdu.

const GLOBAL_ATTRIBUTES = new Set(["title", "dir", "lang", "align"]);

const TAG_ATTRIBUTES = {
    a: ["href", "target"],
    img: ["src", "alt", "width", "height"],
    table: ["border", "cellspacing", "cellpadding", "width", "bgcolor"],
    td: ["colspan", "rowspan", "valign", "bgcolor", "width", "height"],
    th: ["colspan", "rowspan", "valign", "bgcolor", "width", "height", "scope"],
    tr: ["valign", "bgcolor"],
    col: ["span", "width"],
    colgroup: ["span"],
    ol: ["start", "type", "reversed"],
    ul: ["type"],
    li: ["value"],
    details: ["open"],
};

const NUMERIC_ATTRIBUTES = new Set([
    "width", "height", "border", "cellspacing", "cellpadding",
    "colspan", "rowspan", "span", "start", "value",
]);

const KEYWORD_ATTRIBUTES = {
    dir: ["ltr", "rtl", "auto"],
    align: ["left", "right", "center", "justify"],
    valign: ["top", "middle", "bottom", "baseline"],
    scope: ["row", "col", "rowgroup", "colgroup"],
    type: ["1", "a", "A", "i", "I", "disc", "circle", "square"],
};

const CALLOUT_CLASSES = ["warning", "info", "question"];

// Une ancre posée par l'éditeur (<a id="partie-2">) garde son nom, mais sous
// ce préfixe : un identifiant choisi par un membre ne doit pas pouvoir
// prendre la place d'un élément de la page ni d'une variable du navigateur.
// Les liens du même contenu qui la visent (#partie-2) sont réécrits pareil.
const ANCHOR_PREFIX = "user-content-";
const ANCHOR_NAME = /^[A-Za-z][\w:.-]{0,80}$/;

// Propriétés de style gardées : celles de la mise en forme du texte et des
// tableaux. Tout ce qui sort un élément de sa place ou peint hors de sa boîte
// (position, transform, z-index, inset, ombres, contour…) est écarté : un
// message ne doit pas pouvoir se poser par-dessus le reste de la page.
const STYLE_PROPERTY = new RegExp(
    "^(?:" +
        [
            "color",
            "background(?:-color)?",
            "font(?:-[a-z-]+)?",
            "text-(?:align|decoration[a-z-]*|indent|transform|wrap[a-z-]*)",
            "line-height",
            "letter-spacing",
            "word-spacing",
            "word-break",
            "overflow-wrap",
            "white-space[a-z-]*",
            "vertical-align",
            "list-style(?:-type)?",
            "border(?:-(?:top|right|bottom|left))?(?:-(?:color|width|style|radius))?",
            "border-(?:collapse|spacing|radius)",
            "border-(?:top|bottom)-(?:left|right)-radius",
            "padding(?:-[a-z-]+)?",
            "margin(?:-[a-z-]+)?",
            "(?:max-|min-)?(?:width|height)",
            "display",
            "float",
            "clear",
            "overflow(?:-[xy])?",
            "table-layout",
            "caption-side",
            "box-sizing",
            "opacity",
            "position",
        ].join("|") +
        ")$",
);

const SAFE_POSITIONS = ["static", "relative", "initial", "inherit", "unset"];
const SAFE_DISPLAYS = [
    "block", "inline", "inline-block", "none", "list-item", "table",
    "table-row", "table-cell", "table-header-group", "table-row-group",
    "table-footer-group", "table-column", "table-column-group",
    "table-caption", "flex", "inline-flex", "grid", "contents",
    "initial", "inherit", "unset",
];

// Seules ces fonctions sont admises dans une valeur de style. url(), var(),
// calc(), expression() et les autres n'ont rien à faire dans un message.
const SAFE_STYLE_FUNCTIONS = ["rgb", "rgba", "hsl", "hsla"];

// Au-delà, on coupe : un arbre trop profond ou trop gros n'est pas un message.
const MAX_DEPTH = 100;
const MAX_ATTRIBUTE_LENGTH = 2000;
// Une image collée dans l'éditeur arrive en data:, d'où une limite large.
const MAX_DATA_IMAGE_LENGTH = 3000000;

// Les navigateurs ignorent ces caractères en lisant le schéma d'une adresse :
// caractères de contrôle, espaces, espaces de largeur nulle, séparateurs de
// ligne. Écrit en numéros de caractères pour qu'aucun n'ait à figurer ici.
function withoutIgnoredCharacters(value) {
    let result = "";
    for (const character of value) {
        const code = character.codePointAt(0);
        const ignored =
            code <= 0x20 ||
            (code >= 0x7f && code <= 0x9f) ||
            (code >= 0x200b && code <= 0x200f) ||
            code === 0x2028 ||
            code === 0x2029 ||
            code === 0xfeff;
        if (!ignored) result += character;
    }
    return result;
}

/**
 * L'adresse si elle est sans danger, sinon null. Relatives, ancres, http(s),
 * mailto et tel passent ; javascript:, data:, vbscript:, file: et les autres
 * schémas sont refusés. Une image accepte en plus data:image (collage).
 */
function safeUrl(value, { image = false } = {}) {
    if (typeof value !== "string") return null;
    const compact = withoutIgnoredCharacters(value);
    if (!compact) return null;

    const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(compact);
    if (!scheme) {
        return compact.length <= MAX_ATTRIBUTE_LENGTH ? value.trim() : null;
    }

    const name = scheme[1].toLowerCase();
    if (name === "http" || name === "https") {
        return compact.length <= MAX_ATTRIBUTE_LENGTH ? value.trim() : null;
    }
    if (!image && (name === "mailto" || name === "tel")) {
        return compact.length <= MAX_ATTRIBUTE_LENGTH ? value.trim() : null;
    }
    if (
        image &&
        compact.length <= MAX_DATA_IMAGE_LENGTH &&
        /^data:image\/(?:png|jpe?g|gif|webp|avif);base64,[a-z0-9+/=]+$/i.test(compact)
    ) {
        return compact;
    }
    return null;
}

function safeStyle(value) {
    if (typeof value !== "string" || value.length > MAX_ATTRIBUTE_LENGTH) {
        return "";
    }
    // Une barre oblique inversée sert à déguiser un mot-clé, un commentaire à
    // le couper en deux : dans un style de message, ni l'un ni l'autre.
    if (value.includes("\\") || value.includes("/*")) return "";

    const kept = [];
    for (const declaration of value.split(";")) {
        const separator = declaration.indexOf(":");
        if (separator === -1) continue;

        const property = declaration.slice(0, separator).trim().toLowerCase();
        const propertyValue = declaration.slice(separator + 1).trim();
        if (!property || !propertyValue) continue;
        if (!STYLE_PROPERTY.test(property)) continue;

        const lowered = propertyValue.toLowerCase();
        if (/[<>{}@]/.test(lowered) || lowered.includes("javascript:")) continue;

        const functions = lowered.match(/[a-z-]+(?=\s*\()/g) || [];
        if (functions.some((name) => !SAFE_STYLE_FUNCTIONS.includes(name))) {
            continue;
        }

        if (property === "position" && !SAFE_POSITIONS.includes(lowered)) continue;
        if (property === "display" && !SAFE_DISPLAYS.includes(lowered)) continue;
        // Une marge négative fait glisser un élément sur son voisin.
        if (
            (property.startsWith("margin") || property === "text-indent") &&
            /(?:^|[\s,])-\s*[\d.]/.test(lowered)
        ) {
            continue;
        }

        kept.push(`${property}: ${propertyValue}`);
    }
    return kept.join("; ");
}

function safeClass(tag, value) {
    if (typeof value !== "string") return "";
    return value
        .split(/\s+/)
        .filter((token) => {
            if (token === "sr-only") return true;
            if (tag === "pre" || tag === "code") {
                return /^language-[a-z0-9+#.-]{1,30}$/i.test(token);
            }
            if (tag === "blockquote") return CALLOUT_CLASSES.includes(token);
            return false;
        })
        .join(" ");
}

function safeAttributes(tag, attribs, anchors) {
    const cleaned = {};
    const allowed = TAG_ATTRIBUTES[tag] || [];
    let opensNewTab = false;

    for (const [rawName, rawValue] of Object.entries(attribs || {})) {
        const name = rawName.toLowerCase();
        const value = typeof rawValue === "string" ? rawValue : "";

        if (name === "style") {
            const style = safeStyle(value);
            if (style) cleaned.style = style;
            continue;
        }
        if (name === "class") {
            const className = safeClass(tag, value);
            if (className) cleaned.class = className;
            continue;
        }
        // Le marqueur que transformHtml pose lui-même à la place d'un bloc de
        // code. Il ne désigne qu'un bloc déjà calculé pour ce message.
        if (
            tag === "div" &&
            name === "data-code-block-id" &&
            /^code-block-\d+$/.test(value)
        ) {
            cleaned[name] = value;
            continue;
        }
        if (tag === "a" && name === "id") {
            if (ANCHOR_NAME.test(value) && !value.startsWith(ANCHOR_PREFIX)) {
                cleaned.id = ANCHOR_PREFIX + value;
                anchors.add(value);
            }
            continue;
        }
        if (!GLOBAL_ATTRIBUTES.has(name) && !allowed.includes(name)) continue;
        if (value.length > MAX_ATTRIBUTE_LENGTH && name !== "src") continue;

        if (name === "href") {
            const url = safeUrl(value);
            if (url) cleaned.href = url;
            continue;
        }
        if (name === "src") {
            const url = safeUrl(value, { image: true });
            if (url) cleaned.src = url;
            continue;
        }
        if (name === "target") {
            if (value === "_blank") opensNewTab = true;
            continue;
        }
        if (name === "open" || name === "reversed") {
            cleaned[name] = "";
            continue;
        }
        if (name === "bgcolor") {
            if (/^#?[a-z0-9]{1,20}$/i.test(value)) cleaned[name] = value;
            continue;
        }
        if (KEYWORD_ATTRIBUTES[name]) {
            const keyword = name === "type" ? value : value.toLowerCase();
            if (KEYWORD_ATTRIBUTES[name].includes(keyword)) cleaned[name] = keyword;
            continue;
        }
        if (NUMERIC_ATTRIBUTES.has(name)) {
            if (/^\d{1,5}%?$/.test(value)) cleaned[name] = value;
            continue;
        }
        // title, alt, lang : du texte, que React échappe à l'affichage.
        cleaned[name] = value;
    }

    if (tag === "a" && opensNewTab && cleaned.href) {
        cleaned.target = "_blank";
        cleaned.rel = "noopener noreferrer";
    }
    return cleaned;
}

function isElement(node) {
    return node.type === "tag" || node.type === "script" || node.type === "style";
}

function sanitizeChildren(nodes, parent, depth, anchors) {
    const kept = [];

    for (const node of nodes || []) {
        if (!node) continue;

        if (node.type === "text") {
            kept.push(node);
            continue;
        }
        // Commentaires, directives, CDATA : rien à afficher.
        if (!isElement(node)) continue;

        const tag = String(node.name || "").toLowerCase();
        if (DROPPED_TAGS.has(tag) || depth >= MAX_DEPTH) continue;

        const children = sanitizeChildren(
            node.children,
            node,
            depth + 1,
            anchors,
        );

        if (!ALLOWED_TAGS.has(tag)) {
            // Balise retirée, contenu gardé : il remonte d'un cran.
            kept.push(...children);
            continue;
        }

        node.name = tag;
        node.attribs = safeAttributes(tag, node.attribs, anchors);
        node.children = children;
        kept.push(node);
    }

    // html-react-parser lit `parent` pour décider si un blanc a sa place
    // (entre deux lignes de tableau, non) : on le remet à jour, avec ses voisins.
    kept.forEach((node, index) => {
        node.parent = parent;
        node.prev = kept[index - 1] || null;
        node.next = kept[index + 1] || null;
    });
    return kept;
}

/**
 * Nettoie un arbre rendu par `htmlToDOM` (html-react-parser) et le renvoie.
 * Les nœuds gardés sont modifiés sur place.
 *
 * @param {Array<object>} nodes
 * @returns {Array<object>}
 */
export function sanitizeHtmlTree(nodes) {
    if (!Array.isArray(nodes)) return [];

    const anchors = new Set();
    const tree = sanitizeChildren(nodes, null, 0, anchors);
    if (anchors.size) pointLinksToAnchors(tree, anchors);
    return tree;
}

// Les liens internes suivent leurs ancres renommées. Un lien vers un titre
// (identifiant posé au rendu par transformHtml) n'est pas une ancre de
// l'auteur : il reste tel quel.
function pointLinksToAnchors(nodes, anchors) {
    for (const node of nodes) {
        if (!isElement(node)) continue;
        const href = node.name === "a" ? node.attribs.href : null;
        if (href && href.startsWith("#") && anchors.has(href.slice(1))) {
            node.attribs.href = `#${ANCHOR_PREFIX}${href.slice(1)}`;
        }
        pointLinksToAnchors(node.children || [], anchors);
    }
}
